/* Local source overlay. Shopify rendering and editor durability require S2P proof. */
(() => {
  'use strict';
  const namespace = '__kwNativePagesV1';
  if (window[namespace]) return;
  window[namespace] = { installed: true };
  const WRAPPERS = 'div.kw-native-page--faq, div.kw-native-page--howto, div.kw-native-page--shops';
  const controllers = new WeakMap();
  const pending = new WeakMap();
  let editorViewport = null;
  let editorViewportToken = 0;
  let stableEditorViewport = window.Shopify?.designMode === true
    ? { x: window.scrollX, y: window.scrollY }
    : null;
  let stableEditorViewportTimer = 0;
  let restoringEditorViewport = false;

  const wrapperFor = target => target instanceof Element ? target.closest(WRAPPERS) : null;
  const all = (root, selector) => Array.from(root.querySelectorAll(selector));
  const cancelPending = root => {
    if (pending.has(root)) {
      window.cancelAnimationFrame(pending.get(root).frame);
      pending.delete(root);
    }
  };

  const cancelStableEditorViewportTimer = () => {
    if (!stableEditorViewportTimer) return;
    window.clearTimeout(stableEditorViewportTimer);
    stableEditorViewportTimer = 0;
  };

  const rememberStableEditorViewport = () => {
    if (window.Shopify?.designMode !== true) return;
    if (restoringEditorViewport && editorViewport) {
      const snapshot = editorViewport;
      if (window.performance.now() <= snapshot.expiresAt
        && (Math.abs(window.scrollY - snapshot.y) > 0.5 || Math.abs(window.scrollX - snapshot.x) > 0.5)) {
        window.requestAnimationFrame(() => {
          if (restoringEditorViewport && editorViewport?.token === snapshot.token) {
            window.scrollTo({ left: snapshot.x, top: snapshot.y, behavior: 'instant' });
          }
        });
      }
      return;
    }
    cancelStableEditorViewportTimer();
    stableEditorViewportTimer = window.setTimeout(() => {
      stableEditorViewportTimer = 0;
      if (restoringEditorViewport) return;
      stableEditorViewport = { x: window.scrollX, y: window.scrollY };
    }, 140);
  };

  const captureEditorViewport = event => {
    if (window.Shopify?.designMode !== true || !wrapperFor(event.target)) return;
    const automaticSelection = (event.type === 'shopify:block:select' || event.type === 'shopify:section:select')
      && event.detail?.load === true;
    const lifecycle = event.type === 'shopify:section:unload' || event.type === 'shopify:section:load' || event.type === 'shopify:section:reorder';
    if (!lifecycle && !automaticSelection) return;
    const now = window.performance.now();
    // An unload snapshot is the last reliable position before Shopify replaces a section.
    // Do not overwrite it if the matching load arrives after the platform has already moved the preview.
    if (event.type === 'shopify:section:load' && editorViewport && now <= editorViewport.expiresAt) return;
    // Shopify can move the preview before emitting a load/select event. Cancel the
    // pending scroll debounce so that platform jump never becomes the stable position.
    cancelStableEditorViewportTimer();
    const position = event.type === 'shopify:section:unload'
      ? { x: window.scrollX, y: window.scrollY }
      : (stableEditorViewport || { x: window.scrollX, y: window.scrollY });
    editorViewport = {
      x: position.x,
      y: position.y,
      expiresAt: now + 2000,
      token: ++editorViewportToken
    };
  };

  const restoreEditorViewport = () => {
    const snapshot = editorViewport;
    if (!snapshot || window.performance.now() > snapshot.expiresAt) {
      editorViewport = null;
      restoringEditorViewport = false;
      return;
    }
    cancelStableEditorViewportTimer();
    restoringEditorViewport = true;
    stableEditorViewport = { x: snapshot.x, y: snapshot.y };
    const restore = () => {
      if (editorViewport?.token !== snapshot.token || window.performance.now() > snapshot.expiresAt) return;
      if (Math.abs(window.scrollY - snapshot.y) > 0.5 || Math.abs(window.scrollX - snapshot.x) > 0.5) {
        window.scrollTo({ left: snapshot.x, top: snapshot.y, behavior: 'instant' });
      }
    };
    window.requestAnimationFrame(() => {
      restore();
      window.requestAnimationFrame(restore);
    });
    window.setTimeout(restore, 120);
    window.setTimeout(() => {
      if (editorViewport?.token === snapshot.token) {
        editorViewport = null;
        restoringEditorViewport = false;
      }
    }, 1000);
  };

  class SliderController {
    constructor(page, root, initialLeft = 0) {
      this.page = page;
      this.root = root;
      this.track = root.querySelector('[data-kw-slider-track]');
      if (!this.track || !this.track.children.length) return;
      const track = this.track;
      this.controls = document.createElement('div');
      this.controls.className = 'kw-slider-controls';
      this.controls.setAttribute('data-control-style', root.dataset.controlStyle || 'round');
      const button = (label, text) => {
        const node = document.createElement('button');
        node.type = 'button'; node.textContent = text;
        node.setAttribute('aria-label', label);
        node.setAttribute('aria-controls', track.id);
        this.controls.append(node); return node;
      };
      this.previous = button('前のスライド', '‹');
      this.pagination = document.createElement('div');
      this.pagination.className = 'kw-slider-pagination';
      this.controls.append(this.pagination);
      this.next = button('次のスライド', '›');
      root.append(this.controls);
      page.cleanups.push(() => this.controls.remove());
      page.listen(this.previous, 'click', () => this.move(-1));
      page.listen(this.next, 'click', () => this.move(1));
      page.listen(this.pagination, 'click', event => {
        const index = Number(event.target.getAttribute('data-slide-page'));
        if (event.target.getAttribute('data-slide-page') !== null && this.positions[index] !== undefined) this.go(this.positions[index]);
      });
      page.listen(track, 'scroll', () => this.update());
      page.listen(track, 'keydown', event => {
        if (event.target !== track || this.mode === 'grid') return;
        if (event.key === 'ArrowLeft') this.move(-1);
        else if (event.key === 'ArrowRight') this.move(1);
        else if (event.key === 'Home') this.go(0);
        else if (event.key === 'End') this.go(this.max);
        else return;
        event.preventDefault();
      });
      page.listen(track, 'focusin', event => this.reveal(event.target));
      page.listen(window, 'resize', () => this.measure());
      if (window.ResizeObserver) {
        const observer = new window.ResizeObserver(() => this.measure());
        observer.observe(track);
        page.cleanups.push(() => observer.disconnect());
      }
      this.measure();
      if (Number.isFinite(initialLeft)) this.go(initialLeft);
    }
    measure() {
      if (!this.track || this.track.clientWidth <= 0) return;
      const viewport = window.innerWidth;
      const slot = viewport < 767.5 ? 'mobile' : viewport < 1024.5 ? 'tablet' : viewport < 1199.5 ? 'laptop' : 'desktop';
      const name = slot[0].toUpperCase() + slot.slice(1);
      this.mode = this.root.dataset['mode' + name] || 'scroll';
      this.max = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const width = this.track.children[0].getBoundingClientRect().width;
      this.step = Math.max(1, width * Math.max(1, Math.min(10, Number(this.root.dataset['step' + name]) || 1)));
      this.positions = [0];
      for (let at = this.step; at < this.max - 1; at += this.step) this.positions.push(at);
      if (this.max > 0) this.positions.push(this.max);
      const count = this.positions.length;
      if (this.pagination.children.length !== count) {
        Array.from(this.pagination.children).forEach(child => child.remove());
        this.positions.forEach((position, index) => {
          const dot = document.createElement('button'); dot.type = 'button';
          dot.setAttribute('data-slide-page', index);
          dot.setAttribute('aria-label', 'スライドページ ' + (index + 1));
          dot.setAttribute('aria-controls', this.track.id);
          dot.textContent = String(index + 1); this.pagination.append(dot);
        });
      }
      this.controls.hidden = this.mode === 'grid' || this.max <= 1;
      this.previous.hidden = this.next.hidden = this.root.dataset.showControls === 'false';
      this.pagination.hidden = this.root.dataset.showPagination === 'false';
      this.go(this.mode === 'grid' ? 0 : this.track.scrollLeft);
    }
    go(position) {
      if (!this.track || this.max === undefined) return;
      const next = Math.max(0, Math.min(this.max, position));
      if (Math.abs(this.track.scrollLeft - next) > 0.5) this.track.scrollTo({ left: next, top: 0, behavior: 'instant' });
      this.update();
    }
    move(direction) {
      this.measure();
      if (this.mode !== 'grid') this.go(this.track.scrollLeft + direction * this.step);
    }
    update() {
      if (this.max === undefined) return;
      const left = Math.max(0, Math.min(this.max, this.track.scrollLeft));
      this.previous.disabled = left <= 1;
      this.next.disabled = left >= this.max - 1;
      let active = 0;
      this.positions.forEach((position, index) => { if (Math.abs(position-left)<Math.abs(this.positions[active]-left)) active=index; });
      Array.from(this.pagination.children).forEach((dot,index) => dot.setAttribute('aria-current', index===active ? 'true' : 'false'));
    }
    reveal(target) {
      if (!this.track?.contains(target)) return;
      this.measure();
      if (this.mode === 'grid' || this.max === undefined) return;
      const rect=target.getBoundingClientRect(), bounds=this.track.getBoundingClientRect();
      const left=bounds.left+this.track.clientLeft, right=left+this.track.clientWidth;
      const delta=rect.left<left ? rect.left-left : rect.right>right ? rect.right-right : 0;
      if(delta) this.go(this.track.scrollLeft+delta);
    }
  }

  class PageController {
    constructor(root, previous = {}) {
      this.root = root;
      this.abort = new AbortController();
      this.cleanups = [];
      this.panels = all(root, '[data-kw-shop-panel]');
      this.buttons = [];
      this.sliders = [];
      this.faqGroups = [];
      this.selectedId = previous.selectedId || null;
      this.sliderPositions = previous.sliderPositions || {};
      this.failed = false;
      try {
        this.enhanceTabs();
        this.enhanceRegions();
        this.enhanceFaq();
        this.enhanceSliders();
        if (previous.focusPanelId) {
          const index = this.panels.findIndex(panel => panel.id === previous.focusPanelId);
          if (index >= 0 && this.buttons[index]) this.focusTab(index);
          else if (this.buttons.length) this.focusTab(Math.max(0, this.panels.findIndex(panel => panel.id === this.selectedId)));
        }
      } catch (error) {
        this.dispose();
        this.failed = true;
        // The plain labels and every content panel remain available on failure.
      }
    }

    listen(node, type, handler) {
      node.addEventListener(type, handler, { signal: this.abort.signal });
    }

    uniqueId(node) {
      return Boolean(node.id) && all(this.root, '[id]').filter(item => item.id === node.id).length === 1;
    }

    navigation(selector) {
      const nav = this.root.querySelector(selector);
      if (!nav) return null;
      const fallback = nav.querySelector('[data-kw-nav-fallback]');
      const holder = nav.querySelector('[data-kw-nav-controls]');
      if (!this.uniqueId(nav) || !fallback || !holder) throw new Error('Incomplete native navigation');
      return { nav, fallback, holder };
    }

    mountNavigation(parts, controls) {
      // Remove only the generated subtree; merchant content is never replaced.
      this.cleanups.push(() => {
        controls.remove();
        parts.fallback.hidden = false;
        parts.nav.removeAttribute('data-kw-enhanced');
      });
      parts.holder.append(controls);
      parts.fallback.hidden = true;
      parts.nav.setAttribute('data-kw-enhanced', 'true');
    }

    enhanceTabs() {
      const parts = this.navigation('[data-kw-partner-nav]');
      if (!parts || !this.panels.length) return;
      if (this.panels.some(panel => !this.uniqueId(panel) || !panel.dataset.tabLabel?.trim())) {
        throw new Error('Incomplete native panels');
      }
      const tablist = document.createElement('div');
      tablist.className = 'kw-shop-tabs';
      tablist.setAttribute('role', 'tablist');
      tablist.setAttribute('aria-label', parts.nav.getAttribute('aria-label') || '取扱カテゴリー');
      this.buttons = this.panels.map((panel, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'kw-shop-tab';
        button.id = 'kw-tab-' + parts.nav.id + '-' + panel.id;
        button.textContent = panel.dataset.tabLabel;
        button.setAttribute('role', 'tab');
        button.setAttribute('aria-controls', panel.id);
        button.setAttribute('aria-selected', 'false');
        button.tabIndex = -1;
        this.listen(button, 'click', () => this.activate(index, true));
        this.listen(button, 'keydown', event => {
          let next = index;
          if (event.key === 'ArrowRight') next = (index + 1) % this.buttons.length;
          else if (event.key === 'ArrowLeft') next = (index + this.buttons.length - 1) % this.buttons.length;
          else if (event.key === 'Home') next = 0;
          else if (event.key === 'End') next = this.buttons.length - 1;
          else if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.activate(index, true);
            return;
          } else return;
          event.preventDefault();
          this.activate(next, true);
        });
        tablist.append(button);
        return button;
      });
      // Commit roles/visibility only after every control and handler is prepared.
      this.cleanups.push(() => this.panels.forEach(panel => {
        panel.hidden = false;
        panel.removeAttribute('role');
        panel.removeAttribute('aria-labelledby');
        panel.removeAttribute('tabindex');
      }));
      this.mountNavigation(parts, tablist);
      this.panels.forEach((panel, index) => {
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', this.buttons[index].id);
        panel.tabIndex = 0;
      });
      const selected = this.panels.findIndex(panel => panel.id === this.selectedId);
      this.activate(selected < 0 ? 0 : selected);
    }

    activate(index, focus = false) {
      const chosen = this.panels[index];
      if (!chosen || !this.buttons[index] || !this.root.contains(chosen)) return;
      const focusedPanel = this.panels.find(panel => panel.contains(document.activeElement));
      this.panels.forEach((panel, position) => {
        const selected = position === index;
        panel.hidden = !selected;
        this.buttons[position].setAttribute('aria-selected', String(selected));
        this.buttons[position].tabIndex = selected ? 0 : -1;
      });
      this.selectedId = chosen.id;
      this.sliders.forEach(slider => slider.measure());
      if (focus || (focusedPanel && focusedPanel !== chosen)) this.focusTab(index);
    }

    focusTab(index) {
      const selected = this.buttons[index];
      if (!selected) return;
      this.buttons.forEach((button, position) => { button.tabIndex = position === index ? 0 : -1; });
      selected.focus({ preventScroll: true });
      const strip = selected.parentElement;
      if (!strip || strip.clientWidth <= 0) return;
      const left = strip.getBoundingClientRect().left + strip.clientLeft;
      const right = left + strip.clientWidth;
      const tab = selected.getBoundingClientRect();
      const distance = tab.left < left ? tab.left - left : tab.right > right ? tab.right - right : 0;
      // Reveal only the clipped horizontal edge; keep the page and vertical position still.
      // Instant movement also respects reduced motion when a base theme enables smooth scroll.
      if (distance) strip.scrollBy({ left: distance, top: 0, behavior: 'instant' });
    }

    enhanceRegions() {
      const parts = this.navigation('[data-kw-region-nav]');
      const candidates = all(this.root, '[data-kw-shop-region]');
      if (!parts || !candidates.length) return;
      // Theme Editor inserts a blank region while a merchant is adding content.
      // Ignore only incomplete candidates so one draft section never removes every valid link.
      const regions = candidates.filter(region => (
        this.uniqueId(region)
        && region.dataset.regionCode?.trim()
        && region.dataset.regionLabel?.trim()
      ));
      if (!regions.length) return;
      const list = document.createElement('ul');
      list.className = 'kw-shop-region-links';
      const codes = new Set();
      regions.forEach(region => {
        if (codes.has(region.dataset.regionCode)) return;
        codes.add(region.dataset.regionCode);
        const item = document.createElement('li');
        const heading = document.createElement('h3');
        const enabled = region.dataset.regionLinkEnabled === 'true';
        const link = document.createElement(enabled ? 'a' : 'span');
        if (enabled) link.href = '#' + encodeURIComponent(region.id);
        link.textContent = region.dataset.regionLabel;
        if (enabled) this.listen(link, 'click', () => {
          if (this.root.contains(region)) region.focus({ preventScroll: true });
        });
        heading.append(link);
        item.append(heading);
        const prefectureLabels = [];
        const seenPrefectureLabels = new Set();
        regions
          .filter(candidate => candidate.dataset.regionCode === region.dataset.regionCode)
          .forEach(candidate => {
            all(candidate, '[data-kw-prefecture-label]').forEach(prefecture => {
              const sourceLabel = prefecture.dataset.kwPrefectureLabel?.trim();
              const label = sourceLabel?.split('/')[0].trim();
              if (label && !seenPrefectureLabels.has(label)) {
                seenPrefectureLabels.add(label);
                prefectureLabels.push(label);
              }
            });
          });
        const regionSubtitle = prefectureLabels.length
          ? prefectureLabels.join('/')
          : region.dataset.regionSubtitle?.trim();
        if (regionSubtitle) {
          const subtitle = document.createElement('h4');
          subtitle.textContent = regionSubtitle;
          item.append(subtitle);
        }
        list.append(item);
        const priorTabindex = region.getAttribute('tabindex');
        this.cleanups.push(() => {
          if (priorTabindex === null) region.removeAttribute('tabindex');
          else region.setAttribute('tabindex', priorTabindex);
        });
        region.tabIndex = -1;
      });
      this.mountNavigation(parts, list);
    }

    enhanceFaq() {
      this.faqGroups = all(this.root, '[data-kw-faq]').map(section => all(section, '.kw-faq-row'));
      this.faqGroups.forEach(rows => rows.forEach(row => this.listen(row, 'toggle', () => {
        if (row.open) rows.forEach(other => { if (other !== row) other.open = false; });
      })));
    }

    enhanceSliders() {
      this.sliders = all(this.root, '[data-kw-slider]').map(root => {
        const key = root.dataset.kwSliderKey || root.id;
        return new SliderController(this, root, Number(this.sliderPositions[key]) || 0);
      });
    }

    reveal(target) {
      const index = this.panels.findIndex(panel => panel === target || panel.contains(target) || target.contains(panel));
      if (index >= 0) this.activate(index);
      for (const rows of this.faqGroups) {
        const chosen = rows.find(row => row === target || row.contains(target));
        if (chosen) rows.forEach(row => { row.open = row === chosen; });
      }
      this.sliders.forEach(slider => slider.reveal(target));
    }

    snapshot() {
      const focusIndex = this.buttons.indexOf(document.activeElement);
      return {
        selectedId: this.selectedId,
        focusPanelId: focusIndex >= 0 ? this.panels[focusIndex]?.id : null,
        sliderPositions: Object.fromEntries(this.sliders
          .filter(slider => slider.track)
          .map(slider => [slider.root.dataset.kwSliderKey || slider.root.id, slider.track.scrollLeft]))
      };
    }

    dispose() {
      this.abort.abort();
      this.cleanups.reverse().forEach(cleanup => cleanup());
      this.cleanups = [];
      this.buttons = [];
      // Also restore any panel discovered before a partial initialization failure.
      this.panels.forEach(panel => { panel.hidden = false; });
    }
  }

  function rebuild(root, previous) {
    if (!root) return;
    const saved = previous || pending.get(root)?.snapshot || controllers.get(root)?.snapshot() || {};
    cancelPending(root);
    controllers.get(root)?.dispose();
    controllers.delete(root);
    if (!root.isConnected) return;
    controllers.set(root, new PageController(root, saved));
  }

  function scheduleRebuild(root, previous) {
    if (!root || pending.has(root)) return;
    const snapshot = previous || controllers.get(root)?.snapshot() || {};
    const frame = window.requestAnimationFrame(() => {
      pending.delete(root);
      rebuild(root, snapshot);
    });
    pending.set(root, { frame, snapshot });
  }

  function handleEditor(event) {
    const root = wrapperFor(event.target);
    if (!root) return;
    if (event.type === 'shopify:section:unload') {
      const snapshot = pending.get(root)?.snapshot || controllers.get(root)?.snapshot() || {};
      controllers.get(root)?.dispose();
      controllers.delete(root);
      scheduleRebuild(root, snapshot);
    } else if (event.type === 'shopify:section:load' || event.type === 'shopify:section:reorder') {
      scheduleRebuild(root);
      restoreEditorViewport();
    } else {
      if (event.detail?.load === true) {
        scheduleRebuild(root);
        restoreEditorViewport();
        return;
      }
      if (pending.has(root)) {
        const snapshot = pending.get(root).snapshot;
        cancelPending(root);
        rebuild(root, snapshot);
      } else if (!controllers.has(root)) rebuild(root);
      controllers.get(root)?.reveal(event.target);
    }
  }

  // One installation per document, even if the runtime asset is evaluated again.
  [
    'shopify:section:load',
    'shopify:section:unload',
    'shopify:section:reorder',
    'shopify:block:select',
    'shopify:section:select'
  ].forEach(type => {
    document.addEventListener(type, captureEditorViewport, true);
    document.addEventListener(type, handleEditor);
  });
  if (window.Shopify?.designMode === true) {
    window.addEventListener('scroll', rememberStableEditorViewport, { passive: true });
  }

  const bootstrap = () => document.querySelectorAll(WRAPPERS).forEach(root => rebuild(root));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bootstrap, { once: true });
  else bootstrap();
})();
